import React, { useEffect } from "react";
import PropTypes from "prop-types";
import ResultHead from "components/molecules/ResultHead";
import ResultFoot from "components/molecules/ResultFoot";
import ResultList from "components/organisms/ResultList";
import HintKeywordSearch from "components/molecules/HintKeywordSearch";
import Hint from "components/atoms/Hint";
import LoadingIndicator from "components/atoms/LoadingIndicator";
import config from "config";
import { debugModeArgument } from "utils/debugModeArgument";
import { SearchResultStructuredData } from "utils/classes/SearchResultStructuredData";

export const RESULT_TYPE = {
  NO_RESPONSE: "no-response",
  NO_RESULT_KEYWORD: "no-result-keyword",
  NO_RESULT_FILTER: "no-result-filter",
  RESULT: "result",
  REDIRECT: "redirect",
};

export const defaultProps = {
  itemsPerPage: 30,
  noResponse: {
    text: `Leider können wir Ihnen derzeit kein Suchergebnis anzeigen, wir bitten um Entschuldigung!
            Bitte versuchen Sie es später erneut.`,
  },
  noResultFilter: {
    text: `Ihre Suche ergab leider keine Treffer.
            Bitte prüfen Sie Ihre Filtereinstellungen`,
  },
  noResultKeyword: {
    text: `Ihre Suche ergab leider keinen Treffer.
            Prüfen Sie Ihre Eingabe oder geben Sie einen allgemeineren Begriff ein`,
    inputSearch: {
      name: "q",
      placeholder: "Erneut suchen",
    },
  },
  externalRedirect: {
    text: "Ihre Suche ergab keinen Treffer im Cornelsen Onlineshop. Möglicherweise finden Sie das Gesuchte unter folgendem Link:",
  },
  showFoot: true,
  showHead: true,
  totalCount: 0,
  type: "result",
  structuredData: {
    name: undefined,
  },
};

const Result = ({
  coverFallbackImg,
  externalRedirect = { ...defaultProps.externalRedirect },
  itemsPerPage = defaultProps.itemsPerPage,
  loading,
  loadingMore,
  focusFirstNewItem,
  offset,
  noResponse = { ...defaultProps.noResponse },
  noResultFilter = { ...defaultProps.noResultFilter },
  noResultKeyword = { ...defaultProps.noResultKeyword },
  onFilterChange,
  onResultMore,
  onFocusFirstNewItemDone,
  records,
  redirect,
  resultInfo,
  showFoot = defaultProps.showFoot,
  showHead = defaultProps.showHead,
  supportingSeriesData,
  totalCount = defaultProps.totalCount,
  type = defaultProps.type,
  structuredData = { ...defaultProps.structuredData },
}) => {
  const renderNoResponse = () => <Hint type="warning">{noResponse.text}</Hint>;
  const [isStructuredDataAdded, setIsStructuredDataAdded] =
    React.useState(false);

  const renderNoResultFilter = () => (
    <Hint type="warning">{noResultFilter.text}</Hint>
  );

  const renderNoResultKeyword = () => (
    <HintKeywordSearch
      type="info"
      text={noResultKeyword.text}
      inputSearch={noResultKeyword.inputSearch}
      onFilterChange={onFilterChange}
    />
  );

  const renderRedirect = () => {
    if (
      config.redirects.immediate.some((pattern) =>
        new RegExp(pattern).test(redirect),
      )
    ) {
      window.location.replace(redirect);
    }

    if (
      config.redirects.external.some((pattern) =>
        new RegExp(pattern).test(redirect),
      )
    ) {
      return (
        <Hint type="info">
          {externalRedirect.text}
          <br />
          <br />
          <a href={redirect} target="_blank" rel="noopener noreferrer">
            {redirect}
          </a>
        </Hint>
      );
    }

    return renderNoResultKeyword();
  };

  useEffect(() => {
    if (
      records &&
      records.length &&
      !isStructuredDataAdded &&
      structuredData.name
    ) {
      createJsonLd();
      setIsStructuredDataAdded(true);
    }
  }, [records]);

  const createJsonLd = () => {
    const searchResultStructuredData = new SearchResultStructuredData(records);
    const url = window.location.origin + window.location.pathname;
    const jsonLd = searchResultStructuredData.getJsonLd(
      url,
      structuredData.name,
    );
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.innerHTML = JSON.stringify(jsonLd);
    document.head.appendChild(script);
  };

  const renderList = () => (
    <div
      className="sal-result"
      data-cv-debug={debugModeArgument("true")}
      data-cv-debug-system={debugModeArgument("SAL")}
      data-cv-debug-component={debugModeArgument("Result")}
    >
      {showHead && (
        <ResultHead
          resultInfo={{
            ...resultInfo,
            count: totalCount,
          }}
        />
      )}

      <ResultList
        records={records}
        coverFallbackImg={coverFallbackImg}
        supportingSeriesData={supportingSeriesData}
        offset={offset}
        focusFirstNewItem={focusFirstNewItem}
        onFocusFirstNewItemDone={onFocusFirstNewItemDone}
      />

      {showFoot && (
        <ResultFoot
          showMoreBtn={offset + itemsPerPage < totalCount}
          moreButton={{
            itemCount: itemsPerPage,
            name: "btn-result-more",
            showCount: false,
            text: "Weitere Ergebnisse laden",
            iconType: "Reload",
          }}
          onResultMore={onResultMore}
          loadingMore={loadingMore}
        />
      )}
    </div>
  );

  if (loading) {
    return (
      <Hint type="info">
        <LoadingIndicator />
      </Hint>
    );
  }

  switch (type) {
    case RESULT_TYPE.NO_RESPONSE:
      return renderNoResponse();
    case RESULT_TYPE.NO_RESULT_FILTER:
      return renderNoResultFilter();
    case RESULT_TYPE.NO_RESULT_KEYWORD:
      return renderNoResultKeyword();
    case RESULT_TYPE.RESULT:
      return renderList();
    case RESULT_TYPE.REDIRECT:
      return renderRedirect();
    default:
      return null;
  }
};

Result.propTypes = {
  coverFallbackImg: PropTypes.string,
  externalRedirect: PropTypes.shape({
    text: PropTypes.string,
  }),
  itemsPerPage: PropTypes.number,
  loading: PropTypes.bool,
  loadingMore: PropTypes.bool,
  focusFirstNewItem: PropTypes.bool,
  offset: PropTypes.number.isRequired,
  noResponse: PropTypes.shape({
    text: PropTypes.string,
  }),
  noResultFilter: PropTypes.shape({
    text: PropTypes.string.isRequired,
  }).isRequired,
  noResultKeyword: PropTypes.shape({
    text: PropTypes.string.isRequired,
    inputSearch: PropTypes.shape({
      name: PropTypes.string.isRequired,
      onMountStateChange: PropTypes.func,
      placeholder: PropTypes.string,
    }),
  }).isRequired,
  onFilterChange: PropTypes.func,
  onQueryChange: PropTypes.func,
  onResultMore: PropTypes.func,
  onFocusFirstNewItemDone: PropTypes.func,
  records: PropTypes.array,
  redirect: PropTypes.string,
  resultInfo: PropTypes.shape({
    resultsText: PropTypes.string,
    resultText: PropTypes.string,
  }),
  showFoot: PropTypes.bool.isRequired,
  showHead: PropTypes.bool.isRequired,
  supportingSeriesData: PropTypes.objectOf(
    PropTypes.shape({
      linkLabel: PropTypes.string,
      linkTarget: PropTypes.string,
      teaserImageSrc: PropTypes.string,
    }),
  ),
  totalCount: PropTypes.number,
  type: PropTypes.oneOf(Object.values(RESULT_TYPE)).isRequired,
  structuredData: PropTypes.shape({
    name: PropTypes.string,
  }),
};

export default Result;
